import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api, formatTicketRef, PendingAction, UpcomingMaintenance } from '../lib/api';
import { 
  AlertCircle, Wrench, Package, 
  ArrowRight, CheckCircle, Timer,
  MapPin, Activity, Clock, Tag,
  Sparkles, ThumbsUp, Meh, AlertTriangle, XCircle,
  History, Building2, Home, Warehouse,
  Truck, Settings, PlayCircle, ChevronRight,
  Euro, TrendingUp, ShoppingCart, ChevronLeft,
  CalendarClock
} from 'lucide-react';
import { PriorityDot } from '../components/DataTable';

export default function Dashboard() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard'],
    queryFn: api.getDashboard,
    refetchInterval: 30000
  });

  const { data: pendingData } = useQuery({
    queryKey: ['pending-actions'],
    queryFn: api.getPendingActions,
    refetchInterval: 30000
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card border-red-200 bg-red-50">
        <div className="flex items-center gap-2 text-red-700">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm font-medium">Errore: {error.message}</span>
        </div>
      </div>
    );
  }

  const { ticketingEnabled, tickets, assets, assetConditions, locations, categories, topLocations, recentTickets, recentAssetEvents, spendingStats, upcomingMaintenances } = data!;

  // Render ticketing-enabled dashboard
  if (ticketingEnabled) {
    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="page-header">
          <div className="flex items-center justify-between w-full sm:block">
            <h1 className="page-title">Dashboard</h1>
            <p className="text-xs text-gray-500 sm:hidden">
              Ultimo aggiornamento: {new Date(data?._timestamp || 0).toLocaleTimeString('it-IT')}
            </p>
          </div>
          <p className="page-subtitle hidden sm:block">
            Ultimo aggiornamento: {new Date(data?._timestamp || 0).toLocaleTimeString('it-IT')}
          </p>
        </div>

        {/* Stats Grid - 2x2 on mobile, 4 cols on desktop */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard
            label="Ticket Aperti"
            value={tickets.open}
            icon={AlertCircle}
            color="amber"
            href="/tickets?status=open"
          />
          <StatCard
            label="In Lavorazione"
            value={tickets.inProgress}
            icon={Wrench}
            color="blue"
            href="/tickets?status=in_progress"
          />
          <StatCard
            label="In Attesa"
            value={tickets.waiting}
            icon={Timer}
            color="orange"
            href="/tickets?status=waiting"
          />
          <StatCard
            label="Risolti"
            value={tickets.resolved}
            icon={CheckCircle}
            color="green"
            href="/tickets?status=resolved"
          />
        </div>

        {/* Two columns layout on desktop */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Recent Tickets */}
          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <Activity className="w-4 h-4 text-gray-500" />
                Ticket Recenti
              </h2>
              <Link to="/tickets" className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1">
                Vedi tutti <ArrowRight className="w-3 h-3" />
              </Link>
            </div>

            {recentTickets.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="w-8 h-8 text-emerald-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Nessun ticket attivo</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {recentTickets.slice(0, 5).map((ticket) => (
                  <Link
                    key={ticket.id}
                    to={`/tickets/${ticket.id}`}
                    className="flex items-center gap-3 py-2 hover:bg-gray-50 -mx-2 px-2 rounded"
                  >
                    <PriorityDot priority={ticket.priority} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {ticket.title}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                        <span className="font-mono text-gray-400">{formatTicketRef(ticket.ticketNumber)}</span>
                        {ticket.location && (
                          <span className="flex items-center gap-1 truncate">
                            <MapPin className="w-3 h-3" />
                            {ticket.location.name}
                          </span>
                        )}
                        {ticket.asset && (
                          <span className="flex items-center gap-1 truncate">
                            <Package className="w-3 h-3" />
                            {ticket.asset.name}
                          </span>
                        )}
                      </div>
                    </div>
                    <StatusBadge status={ticket.status} />
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Asset Overview - Interactive Donut Chart */}
          <AssetStatusWidget assets={assets} />
        </div>

        {/* Pending Actions Widget */}
        {pendingData?.pendingActions && pendingData.stats.total > 0 && (
          <PendingActionsWidget pendingActions={pendingData.pendingActions} stats={pendingData.stats} />
        )}

        {/* Upcoming Maintenances Widget */}
        {upcomingMaintenances && upcomingMaintenances.length > 0 && (
          <UpcomingMaintenancesWidget maintenances={upcomingMaintenances} />
        )}
      </div>
    );
  }

  // Render non-ticketing dashboard (asset-focused)
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center justify-between w-full sm:block">
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle hidden sm:block">
            Ultimo aggiornamento: {new Date(data?._timestamp || 0).toLocaleTimeString('it-IT')}
          </p>
          <p className="text-xs text-gray-500 sm:hidden">
            Ultimo aggiornamento: {new Date(data?._timestamp || 0).toLocaleTimeString('it-IT')}
          </p>
        </div>
      </div>

      {/* Stats Grid - Asset focused */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          label="Totale Asset"
          value={assets.total}
          icon={Package}
          color="blue"
          href="/assets"
        />
        <StatCard
          label="Asset Attivi"
          value={assets.active}
          icon={CheckCircle}
          color="green"
          href="/assets?status=active"
        />
        <StatCard
          label="Ubicazioni"
          value={locations.total}
          icon={MapPin}
          color="purple"
          href="/locations"
        />
        <StatCard
          label="Categorie"
          value={categories}
          icon={Tag}
          color="amber"
          href="/categories"
        />
      </div>

      {/* 4-Column Enterprise Layout: Status, Conditions, Maintenances, Spending */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {/* Asset Status Overview with Interactive Donut Chart */}
        <AssetStatusWidget assets={assets} />

        {/* Asset Conditions Widget */}
        <AssetConditionsWidget conditions={assetConditions} totalAssets={assets.total} />

        {/* Upcoming Maintenances Widget - Compact */}
        <UpcomingMaintenancesCompactWidget maintenances={upcomingMaintenances || []} />

        {/* Spending Stats Widget */}
        <SpendingStatsWidget spendingStats={spendingStats} />
      </div>

      {/* Two columns layout on desktop: Recent Activity + Top Locations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent Asset Events */}
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <History className="w-4 h-4 text-gray-500" />
              Attività Recenti
            </h2>
          </div>

          {recentAssetEvents.length === 0 ? (
            <div className="text-center py-8">
              <History className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">Nessuna attività recente</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {recentAssetEvents.slice(0, 6).map((event) => (
                <div
                  key={event.id}
                  className="flex items-center gap-3 py-2"
                >
                  <div className={`p-2 rounded-lg ${getEventTypeColor(event.action)}`}>
                    {getEventTypeIcon(event.action)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900">
                      <span className="font-medium">{getEventTypeLabel(event.action)}</span>
                      {event.asset && (
                        <Link to={`/assets/${event.asset.id}`} className="text-primary-600 hover:underline ml-1">
                          {event.asset.name}
                        </Link>
                      )}
                    </p>
                    {event.description && (
                      <p className="text-xs text-gray-500 truncate">{event.description}</p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-gray-400">
                      {formatTimeAgo(event.createdAt)}
                    </p>
                    {event.user && (
                      <p className="text-xs text-gray-500">
                        {event.user.firstName} {event.user.lastName}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top Locations */}
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-gray-500" />
              Top Ubicazioni
            </h2>
            <Link to="/locations" className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1">
              Gestisci <ArrowRight className="w-3 h-3" />
            </Link>
          </div>

          {topLocations.length === 0 ? (
            <div className="text-center py-8">
              <MapPin className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">Nessuna ubicazione</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {topLocations.map((location) => (
                <Link
                  key={location.id}
                  to={`/locations/${location.id}`}
                  className="flex items-center gap-3 py-2 hover:bg-gray-50 -mx-2 px-2 rounded"
                >
                  <div className={`p-2 rounded-lg ${getLocationTypeColor(location.type)}`}>
                    {getLocationTypeIcon(location.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {location.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {getLocationTypeLabel(location.type)}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-semibold text-gray-900">{location.assetCount}</span>
                    <p className="text-xs text-gray-500">asset</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Helper functions
function formatTimeAgo(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Ora';
  if (minutes < 60) return `${minutes}m fa`;
  if (hours < 24) return `${hours}h fa`;
  if (days < 7) return `${days}g fa`;
  return new Date(timestamp).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
}

function getEventTypeLabel(action: string): string {
  const labels: Record<string, string> = {
    created: 'Creato',
    updated: 'Aggiornato',
    moved: 'Spostato',
    status_changed: 'Stato cambiato',
    maintenance: 'Manutenzione',
    repair: 'Riparazione',
    disposed: 'Dismesso'
  };
  return labels[action] || action;
}

function getEventTypeIcon(action: string) {
  const icons: Record<string, JSX.Element> = {
    created: <Package className="w-4 h-4 text-emerald-600" />,
    updated: <Activity className="w-4 h-4 text-blue-600" />,
    moved: <MapPin className="w-4 h-4 text-indigo-600" />,
    status_changed: <Activity className="w-4 h-4 text-cyan-600" />,
    maintenance: <Wrench className="w-4 h-4 text-blue-600" />,
    repair: <Wrench className="w-4 h-4 text-orange-600" />,
    disposed: <XCircle className="w-4 h-4 text-red-600" />
  };
  return icons[action] || <Activity className="w-4 h-4 text-gray-600" />;
}

function getEventTypeColor(action: string): string {
  const colors: Record<string, string> = {
    created: 'bg-emerald-100',
    updated: 'bg-blue-100',
    moved: 'bg-indigo-100',
    status_changed: 'bg-cyan-100',
    maintenance: 'bg-blue-100',
    repair: 'bg-orange-100',
    disposed: 'bg-red-100'
  };
  return colors[action] || 'bg-gray-100';
}

function getLocationTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    unit: 'Unità',
    area: 'Area',
    warehouse: 'Magazzino',
    common: 'Comune',
    other: 'Altro'
  };
  return labels[type] || type;
}

function getLocationTypeIcon(type: string) {
  const icons: Record<string, JSX.Element> = {
    unit: <Home className="w-4 h-4 text-blue-600" />,
    area: <MapPin className="w-4 h-4 text-green-600" />,
    warehouse: <Warehouse className="w-4 h-4 text-purple-600" />,
    common: <Building2 className="w-4 h-4 text-amber-600" />,
    other: <MapPin className="w-4 h-4 text-gray-600" />
  };
  return icons[type] || <MapPin className="w-4 h-4 text-gray-600" />;
}

function getLocationTypeColor(type: string): string {
  const colors: Record<string, string> = {
    unit: 'bg-blue-100',
    area: 'bg-green-100',
    warehouse: 'bg-purple-100',
    common: 'bg-amber-100',
    other: 'bg-gray-100'
  };
  return colors[type] || 'bg-gray-100';
}

// Compact Stat Card
interface StatCardProps {
  label: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  color: 'amber' | 'blue' | 'orange' | 'red' | 'green' | 'purple' | 'gray';
  href?: string;
}

function StatCard({ label, value, icon: Icon, color, href }: StatCardProps) {
  const colorClasses = {
    amber: 'text-amber-600 bg-amber-50',
    blue: 'text-blue-600 bg-blue-50',
    orange: 'text-orange-600 bg-orange-50',
    red: 'text-red-600 bg-red-50',
    green: 'text-emerald-600 bg-emerald-50',
    purple: 'text-purple-600 bg-purple-50',
    gray: 'text-gray-600 bg-gray-100',
  };

  const content = (
    <div className="card card-sm hover:border-gray-300 transition-colors">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded ${colorClasses[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div>
          <p className="text-2xl font-semibold text-gray-900">{value}</p>
          <p className="text-xs text-gray-500">{label}</p>
        </div>
      </div>
    </div>
  );

  if (href) {
    return <Link to={href} className="block">{content}</Link>;
  }
  return content;
}

// Asset Status Widget with bar-based layout
interface AssetStatusWidgetProps {
  assets: {
    total: number;
    active: number;
    inRepair: number;
    inMaintenance: number;
    inStorage: number;
    inTransit: number;
  };
}

function AssetStatusWidget({ assets }: AssetStatusWidgetProps) {
  const navigate = useNavigate();

  const statusItems = [
    { key: 'active', label: 'Attivi', count: assets.active, color: 'emerald' as const, icon: CheckCircle },
    { key: 'in_repair', label: 'In Riparazione', count: assets.inRepair, color: 'orange' as const, icon: Wrench },
    { key: 'in_maintenance', label: 'In Manutenzione', count: assets.inMaintenance, color: 'blue' as const, icon: Settings },
    { key: 'in_storage', label: 'In Magazzino', count: assets.inStorage, color: 'purple' as const, icon: Package },
    { key: 'in_transit', label: 'In Transito', count: assets.inTransit, color: 'gray' as const, icon: Truck },
  ];

  const colorClasses: Record<string, { bar: string; icon: string; bg: string; text: string }> = {
    emerald: { bar: 'bg-emerald-500', icon: 'text-emerald-600', bg: 'bg-emerald-100', text: 'text-emerald-700' },
    orange: { bar: 'bg-orange-500', icon: 'text-orange-600', bg: 'bg-orange-100', text: 'text-orange-700' },
    blue: { bar: 'bg-blue-500', icon: 'text-blue-600', bg: 'bg-blue-100', text: 'text-blue-700' },
    purple: { bar: 'bg-purple-500', icon: 'text-purple-600', bg: 'bg-purple-100', text: 'text-purple-700' },
    gray: { bar: 'bg-gray-400', icon: 'text-gray-600', bg: 'bg-gray-100', text: 'text-gray-700' },
  };

  return (
    <div className="card border-blue-200 bg-gradient-to-br from-blue-50/30 to-indigo-50/30">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
            <Package className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Stato Asset</h2>
            <p className="text-xs text-gray-500">
              <span className="font-semibold text-gray-700">{assets.total}</span> totali
            </p>
          </div>
        </div>
        <Link 
          to="/assets"
          className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
        >
          Gestisci <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
      
      {/* Status bars list */}
      <div className="space-y-2">
        {statusItems.map((item) => {
          const colors = colorClasses[item.color];
          const percentage = assets.total > 0 ? Math.round((item.count / assets.total) * 100) : 0;
          const Icon = item.icon;

          return (
            <Link
              key={item.key}
              to={`/assets?status=${item.key}`}
              className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-white/60 transition-colors group"
            >
              <div className={`w-6 h-6 ${colors.bg} rounded flex items-center justify-center flex-shrink-0`}>
                <Icon className={`w-3.5 h-3.5 ${colors.icon}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-xs text-gray-600 group-hover:text-gray-900">{item.label}</span>
                  <span className={`text-xs font-semibold ${colors.text}`}>{item.count}</span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${colors.bar} rounded-full transition-all duration-500`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

// Condition Row with icon
interface ConditionRowProps {
  label: string;
  count: number;
  total: number;
  icon: React.ComponentType<{ className?: string }>;
  color: 'emerald' | 'blue' | 'yellow' | 'orange' | 'red';
}

function ConditionRow({ label, count, total, icon: Icon, color }: ConditionRowProps) {
  const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
  
  const colors = {
    emerald: { bar: 'bg-emerald-500', icon: 'text-emerald-600' },
    blue: { bar: 'bg-blue-500', icon: 'text-blue-600' },
    yellow: { bar: 'bg-yellow-500', icon: 'text-yellow-600' },
    orange: { bar: 'bg-orange-500', icon: 'text-orange-600' },
    red: { bar: 'bg-red-500', icon: 'text-red-600' },
  };

  return (
    <div className="flex items-center gap-3">
      <Icon className={`w-4 h-4 ${colors[color].icon}`} />
      <div className="w-20 text-xs text-gray-600">{label}</div>
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div 
          className={`h-full ${colors[color].bar} rounded-full transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <div className="w-12 text-right text-xs font-medium text-gray-700">{count}</div>
    </div>
  );
}

// Compact Status Badge
function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { className: string; label: string }> = {
    open: { className: 'badge-amber', label: 'Aperto' },
    in_progress: { className: 'badge-blue', label: 'In Corso' },
    waiting: { className: 'badge-orange', label: 'Attesa' },
    resolved: { className: 'badge-green', label: 'Risolto' },
    closed: { className: 'badge-gray', label: 'Chiuso' }
  };

  const c = config[status] || config.open;
  return <span className={`badge text-[10px] ${c.className}`}>{c.label}</span>;
}

// Pending Actions Widget
const actionTypeConfig: Record<string, { label: string; icon: any; color: string; bgColor: string }> = {
  moved: { label: 'Spostamento', icon: Truck, color: 'text-indigo-600', bgColor: 'bg-indigo-100' },
  maintenance: { label: 'Manutenzione', icon: Settings, color: 'text-blue-600', bgColor: 'bg-blue-100' },
  repair: { label: 'Riparazione', icon: Wrench, color: 'text-orange-600', bgColor: 'bg-orange-100' }
};

interface PendingActionsWidgetProps {
  pendingActions: PendingAction[];
  stats: {
    total: number;
    moves: number;
    maintenance: number;
    repairs: number;
    own: number;
  };
}

function PendingActionsWidget({ pendingActions, stats }: PendingActionsWidgetProps) {
  const displayActions = pendingActions.slice(0, 5);

  return (
    <div className="card border-amber-200 bg-gradient-to-br from-amber-50/50 to-orange-50/50">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Azioni in Sospeso</h2>
            <p className="text-xs text-gray-500">
              {stats.total} da completare • {stats.own} tue
            </p>
          </div>
        </div>
        <Link 
          to="/pending-actions" 
          className="text-xs text-amber-700 hover:text-amber-800 font-medium flex items-center gap-1"
        >
          Gestisci <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      {/* Stats row */}
      <div className="flex gap-2 mb-4">
        {stats.moves > 0 && (
          <div className="flex items-center gap-1.5 px-2 py-1 bg-indigo-100 rounded-lg">
            <Truck className="w-3.5 h-3.5 text-indigo-600" />
            <span className="text-xs font-medium text-indigo-700">{stats.moves}</span>
          </div>
        )}
        {stats.maintenance > 0 && (
          <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-100 rounded-lg">
            <Settings className="w-3.5 h-3.5 text-blue-600" />
            <span className="text-xs font-medium text-blue-700">{stats.maintenance}</span>
          </div>
        )}
        {stats.repairs > 0 && (
          <div className="flex items-center gap-1.5 px-2 py-1 bg-orange-100 rounded-lg">
            <Wrench className="w-3.5 h-3.5 text-orange-600" />
            <span className="text-xs font-medium text-orange-700">{stats.repairs}</span>
          </div>
        )}
      </div>

      {/* Action items */}
      <div className="space-y-2">
        {displayActions.map((action) => {
          const config = actionTypeConfig[action.actionType] || actionTypeConfig.maintenance;
          const ActionIcon = config.icon;

          return (
            <Link
              key={action.id}
              to={`/assets/${action.asset.id}`}
              className="flex items-center gap-3 p-2 bg-white/80 hover:bg-white rounded-xl transition-colors"
            >
              <div className={`w-8 h-8 ${config.bgColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
                <ActionIcon className={`w-4 h-4 ${config.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{action.asset.name}</p>
                <p className="text-xs text-gray-500 truncate">
                  {config.label}
                  {action.actionType === 'move' && action.toLocation && ` → ${action.toLocation.name}`}
                </p>
              </div>
              {action.isOwnAction && (
                <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded font-medium">Tu</span>
              )}
              <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
            </Link>
          );
        })}
      </div>

      {stats.total > 5 && (
        <Link
          to="/pending-actions"
          className="mt-3 w-full btn btn-secondary text-sm flex items-center justify-center gap-2"
        >
          Vedi tutte le {stats.total} azioni
          <ArrowRight className="w-4 h-4" />
        </Link>
      )}
    </div>
  );
}

// ============================================================================
// Asset Conditions Widget
// ============================================================================
interface AssetConditionsWidgetProps {
  conditions: {
    excellent: number;
    good: number;
    fair: number;
    poor: number;
    broken: number;
  };
  totalAssets: number;
}

function AssetConditionsWidget({ conditions, totalAssets }: AssetConditionsWidgetProps) {
  const navigate = useNavigate();
  const [selectedCondition, setSelectedCondition] = useState<string | null>(null);

  const conditionItems = [
    { key: 'excellent', label: 'Eccellente', count: conditions.excellent, icon: Sparkles, color: 'emerald' as const },
    { key: 'good', label: 'Buono', count: conditions.good, icon: ThumbsUp, color: 'blue' as const },
    { key: 'fair', label: 'Discreto', count: conditions.fair, icon: Meh, color: 'yellow' as const },
    { key: 'poor', label: 'Scarso', count: conditions.poor, icon: AlertTriangle, color: 'orange' as const },
    { key: 'broken', label: 'Rotto', count: conditions.broken, icon: XCircle, color: 'red' as const },
  ];

  const handleConditionClick = (condition: string | null) => {
    if (condition === selectedCondition) {
      setSelectedCondition(null);
    } else {
      setSelectedCondition(condition);
    }
  };

  const handleNavigate = () => {
    if (selectedCondition) {
      navigate(`/assets?condition=${selectedCondition}`);
    } else {
      navigate('/assets');
    }
  };

  const conditionLabels: Record<string, string> = {
    excellent: 'Eccellente',
    good: 'Buono',
    fair: 'Discreto',
    poor: 'Scarso',
    broken: 'Rotto'
  };

  return (
    <div className="card border-purple-200 bg-gradient-to-br from-purple-50/30 to-pink-50/30">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
            <Activity className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Condizioni Asset</h2>
            <p className="text-xs text-gray-500">
              {selectedCondition ? conditionLabels[selectedCondition] : 'Distribuzione per condizione'}
            </p>
          </div>
        </div>
        <button 
          onClick={handleNavigate}
          className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
        >
          {selectedCondition ? 'Filtra' : 'Dettagli'} <ArrowRight className="w-3 h-3" />
        </button>
      </div>

      <div className="space-y-2">
        {conditionItems.map((item) => {
          const Icon = item.icon;
          const percentage = totalAssets > 0 ? Math.round((item.count / totalAssets) * 100) : 0;
          const isSelected = selectedCondition === item.key;
          const isOtherSelected = selectedCondition !== null && selectedCondition !== item.key;

          const colorClasses: Record<string, { bar: string; icon: string; bg: string }> = {
            emerald: { bar: 'bg-emerald-500', icon: 'text-emerald-600', bg: 'bg-emerald-100' },
            blue: { bar: 'bg-blue-500', icon: 'text-blue-600', bg: 'bg-blue-100' },
            yellow: { bar: 'bg-yellow-500', icon: 'text-yellow-600', bg: 'bg-yellow-100' },
            orange: { bar: 'bg-orange-500', icon: 'text-orange-600', bg: 'bg-orange-100' },
            red: { bar: 'bg-red-500', icon: 'text-red-600', bg: 'bg-red-100' },
          };

          const colors = colorClasses[item.color];

          return (
            <button
              key={item.key}
              onClick={() => handleConditionClick(item.key)}
              className={`w-full flex items-center gap-2 p-1.5 rounded-lg transition-all ${
                isSelected ? 'bg-white ring-1 ring-gray-300 shadow-sm' : isOtherSelected ? 'opacity-40' : 'hover:bg-white/60'
              }`}
            >
              <div className={`w-6 h-6 ${colors.bg} rounded flex items-center justify-center flex-shrink-0`}>
                <Icon className={`w-3.5 h-3.5 ${colors.icon}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-xs text-gray-600">{item.label}</span>
                  <span className="text-xs font-semibold text-gray-700">{item.count}</span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${colors.bar} rounded-full transition-all duration-500`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// Upcoming Maintenances Compact Widget (for 4-column layout)
// ============================================================================
interface UpcomingMaintenancesCompactWidgetProps {
  maintenances: UpcomingMaintenance[];
}

function UpcomingMaintenancesCompactWidget({ maintenances }: UpcomingMaintenancesCompactWidgetProps) {
  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('it-IT', { 
      day: 'numeric', 
      month: 'short'
    });
  };

  const getDaysUntil = (timestamp: number): number => {
    const now = Date.now();
    return Math.ceil((timestamp - now) / (1000 * 60 * 60 * 24));
  };

  // Count urgent maintenances
  const overdueCount = maintenances.filter(m => getDaysUntil(m.nextMaintenanceDate) < 0).length;
  const urgentCount = maintenances.filter(m => {
    const d = getDaysUntil(m.nextMaintenanceDate);
    return d >= 0 && d <= 7;
  }).length;

  const hasIssues = overdueCount > 0 || urgentCount > 0;

  // Show max 10 maintenances (scrollable)
  const displayMaintenances = maintenances.slice(0, 10);

  return (
    <div className={`card ${hasIssues ? 'border-amber-200 bg-gradient-to-br from-amber-50/30 to-orange-50/30' : 'border-cyan-200 bg-gradient-to-br from-cyan-50/30 to-teal-50/30'}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 ${hasIssues ? 'bg-amber-100' : 'bg-cyan-100'} rounded-xl flex items-center justify-center`}>
            <CalendarClock className={`w-5 h-5 ${hasIssues ? 'text-amber-600' : 'text-cyan-600'}`} />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Manutenzioni</h2>
            <p className="text-xs text-gray-500">
              {maintenances.length > 0 ? (
                <>
                  {maintenances.length} in programma
                  {overdueCount > 0 && <span className="text-red-600 font-medium"> • {overdueCount} scadute</span>}
                  {overdueCount === 0 && urgentCount > 0 && <span className="text-amber-600 font-medium"> • {urgentCount} urgenti</span>}
                </>
              ) : 'Nessuna programmata'}
            </p>
          </div>
        </div>
      </div>

      {maintenances.length === 0 ? (
        <div className="text-center py-6">
          <CalendarClock className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-xs text-gray-500">Nessuna manutenzione programmata</p>
        </div>
      ) : (
        <div className="space-y-1.5 max-h-[220px] overflow-y-auto pr-1 -mr-1">
          {displayMaintenances.map((maintenance) => {
            const daysUntil = getDaysUntil(maintenance.nextMaintenanceDate);
            const isOverdue = daysUntil < 0;
            const isUrgent = daysUntil >= 0 && daysUntil <= 7;

            return (
              <Link
                key={maintenance.id}
                to={`/assets/${maintenance.assetId}`}
                className="flex items-center gap-2 p-2 bg-white/70 hover:bg-white rounded-lg transition-colors"
              >
                <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${
                  isOverdue ? 'bg-red-100' : isUrgent ? 'bg-amber-100' : 'bg-cyan-100'
                }`}>
                  {isOverdue ? (
                    <AlertTriangle className="w-3.5 h-3.5 text-red-600" />
                  ) : isUrgent ? (
                    <Clock className="w-3.5 h-3.5 text-amber-600" />
                  ) : (
                    <CalendarClock className="w-3.5 h-3.5 text-cyan-600" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-900 truncate">
                    {maintenance.assetName}
                  </p>
                  <p className="text-[10px] text-gray-500 truncate">
                    {maintenance.description}
                  </p>
                </div>
                <span className={`text-[10px] font-medium whitespace-nowrap ${
                  isOverdue ? 'text-red-600' : isUrgent ? 'text-amber-600' : 'text-gray-500'
                }`}>
                  {formatDate(maintenance.nextMaintenanceDate)}
                </span>
              </Link>
            );
          })}
        </div>
      )}

      {maintenances.length > 10 && (
        <div className="mt-2 text-center">
          <span className="text-[10px] text-gray-500">
            +{maintenances.length - 10} altre
          </span>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Spending Stats Types
// ============================================================================
interface SpendingStats {
  maintenance: {
    week: number;
    month: number;
    year: number;
    total: number;
  };
  purchases: {
    week: number;
    month: number;
    year: number;
    total: number;
  };
}

// ============================================================================
// Spending Stats Widget (only spending, no pending actions)
// ============================================================================
interface SpendingStatsWidgetProps {
  spendingStats?: SpendingStats;
}

function SpendingStatsWidget({ spendingStats }: SpendingStatsWidgetProps) {
  const [spendingPeriod, setSpendingPeriod] = useState<'week' | 'month' | 'year' | 'total'>('month');

  const hasSpendingData = spendingStats && (
    spendingStats.maintenance.total > 0 || spendingStats.purchases.total > 0
  );

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('it-IT', { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const periodLabels: Record<string, string> = {
    week: 'Settimana',
    month: 'Mese',
    year: 'Anno',
    total: 'Totale'
  };

  // If no spending data, show empty state
  if (!hasSpendingData) {
    return (
      <div className="card border-emerald-200 bg-gradient-to-br from-emerald-50/30 to-teal-50/30">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
              <Euro className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Statistiche Spese</h2>
              <p className="text-xs text-gray-500">Manutenzione e acquisti</p>
            </div>
          </div>
        </div>
        <div className="text-center py-8">
          <Euro className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Nessuna spesa registrata</p>
        </div>
      </div>
    );
  }

  // Spending Stats View
  return (
    <div className="card border-emerald-200 bg-gradient-to-br from-emerald-50/30 to-teal-50/30">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
            <Euro className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Statistiche Spese</h2>
            <p className="text-xs text-gray-500">
              Manutenzione e acquisti
            </p>
          </div>
        </div>
      </div>

      {/* Period selector */}
      <div className="flex gap-1 mb-4 bg-white/60 rounded-lg p-1">
        {(['week', 'month', 'year', 'total'] as const).map((period) => (
          <button
            key={period}
            onClick={() => setSpendingPeriod(period)}
            className={`flex-1 py-1.5 px-2 text-xs font-medium rounded-md transition-colors ${
              spendingPeriod === period
                ? 'bg-white text-emerald-700 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {periodLabels[period]}
          </button>
        ))}
      </div>

      {/* Spending cards */}
      {spendingStats && (
        <div className="space-y-3">
          {/* Maintenance costs */}
          <div className="bg-white/70 rounded-xl p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Wrench className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Manutenzione</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatCurrency(spendingStats.maintenance[spendingPeriod])}
                  </p>
                </div>
              </div>
              {spendingPeriod !== 'total' && spendingStats.maintenance.total > 0 && (
                <div className="text-right">
                  <p className="text-[10px] text-gray-400">Totale</p>
                  <p className="text-xs text-gray-500">{formatCurrency(spendingStats.maintenance.total)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Purchase costs */}
          <div className="bg-white/70 rounded-xl p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <ShoppingCart className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Acquisti</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatCurrency(spendingStats.purchases[spendingPeriod])}
                  </p>
                </div>
              </div>
              {spendingPeriod !== 'total' && spendingStats.purchases.total > 0 && (
                <div className="text-right">
                  <p className="text-[10px] text-gray-400">Totale</p>
                  <p className="text-xs text-gray-500">{formatCurrency(spendingStats.purchases.total)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Total row */}
          <div className="pt-2 border-t border-emerald-100">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 font-medium">Totale {periodLabels[spendingPeriod]}</span>
              <span className="text-lg font-bold text-emerald-700">
                {formatCurrency(
                  spendingStats.maintenance[spendingPeriod] + spendingStats.purchases[spendingPeriod]
                )}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Upcoming Maintenances Widget
// ============================================================================
interface UpcomingMaintenancesWidgetProps {
  maintenances: UpcomingMaintenance[];
}

function UpcomingMaintenancesWidget({ maintenances }: UpcomingMaintenancesWidgetProps) {
  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('it-IT', { 
      day: 'numeric', 
      month: 'short',
      year: 'numeric'
    });
  };

  const getDaysUntil = (timestamp: number): number => {
    const now = Date.now();
    return Math.ceil((timestamp - now) / (1000 * 60 * 60 * 24));
  };

  const getStatusBadge = (daysUntil: number) => {
    if (daysUntil < 0) {
      return { label: 'Scaduta', className: 'bg-red-100 text-red-700' };
    } else if (daysUntil <= 7) {
      return { label: 'Urgente', className: 'bg-amber-100 text-amber-700' };
    } else if (daysUntil <= 14) {
      return { label: 'In scadenza', className: 'bg-yellow-100 text-yellow-700' };
    }
    return null;
  };

  // Count urgent maintenances
  const urgentCount = maintenances.filter(m => getDaysUntil(m.nextMaintenanceDate) <= 7).length;

  return (
    <div className={`card ${urgentCount > 0 ? 'border-amber-200 bg-gradient-to-br from-amber-50/30 to-orange-50/30' : 'border-cyan-200 bg-gradient-to-br from-cyan-50/30 to-teal-50/30'}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 ${urgentCount > 0 ? 'bg-amber-100' : 'bg-cyan-100'} rounded-xl flex items-center justify-center`}>
            <CalendarClock className={`w-5 h-5 ${urgentCount > 0 ? 'text-amber-600' : 'text-cyan-600'}`} />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Prossime Manutenzioni</h2>
            <p className="text-xs text-gray-500">
              {maintenances.length} manutenzioni in programma
              {urgentCount > 0 && <span className="text-amber-600 font-medium"> • {urgentCount} urgenti</span>}
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {maintenances.slice(0, 6).map((maintenance) => {
          const daysUntil = getDaysUntil(maintenance.nextMaintenanceDate);
          const statusBadge = getStatusBadge(daysUntil);

          return (
            <Link
              key={maintenance.id}
              to={`/assets/${maintenance.assetId}`}
              className="flex items-center gap-3 p-2.5 bg-white/70 hover:bg-white rounded-xl transition-colors"
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                daysUntil < 0 ? 'bg-red-100' : daysUntil <= 7 ? 'bg-amber-100' : 'bg-cyan-100'
              }`}>
                {daysUntil < 0 ? (
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                ) : daysUntil <= 7 ? (
                  <Clock className="w-4 h-4 text-amber-600" />
                ) : (
                  <CalendarClock className="w-4 h-4 text-cyan-600" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {maintenance.description}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {maintenance.assetName}
                </p>
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <span className="text-xs text-gray-600">{formatDate(maintenance.nextMaintenanceDate)}</span>
                {statusBadge && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${statusBadge.className}`}>
                    {statusBadge.label}
                  </span>
                )}
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
            </Link>
          );
        })}
      </div>

      {maintenances.length > 6 && (
        <div className="mt-3 text-center">
          <span className="text-xs text-gray-500">
            +{maintenances.length - 6} altre manutenzioni in programma
          </span>
        </div>
      )}
    </div>
  );
}
